<template>
  <div class="shop-selection-page">
    <Loader v-if="loaderEnabled" v-bind:logo="loaderLogo"></Loader>

    <b-row class="justify-content-center">
      <b-col md="10" lg="8">
        <KTCodePreview>
          <template v-slot:preview>
            <div class="shop-selection-page__header mb-4">
              <h5 class="mb-2">Chọn gian hàng để kết nối</h5>
              <p class="text-muted">
                Tài khoản của bạn quản lý nhiều gian hàng trên
                {{ platformName }}. Vui lòng chọn gian hàng bạn muốn kết nối với
                ClickBuy.
              </p>
            </div>

            <div v-if="error" class="alert alert-danger" role="alert">
              <div class="d-flex justify-content-between align-items-center">
                <div>{{ error }}</div>
                <b-button variant="danger" size="sm" @click="loadShops">
                  <i class="flaticon2-refresh"></i> Thử lại
                </b-button>
              </div>
            </div>

            <div v-if="loading" class="text-center py-5">
              <b-spinner variant="primary"></b-spinner>
              <p class="text-muted mt-3">Đang tải danh sách gian hàng...</p>
            </div>

            <div
              v-else-if="shops.length > 0"
              class="shop-selection-page__shops"
            >
              <b-card-group deck class="mb-4">
                <b-col
                  v-for="shop in shops"
                  :key="shop.shopId"
                  md="6"
                  lg="4"
                  class="mb-3"
                >
                  <b-card
                    :class="[
                      'shop-card',
                      { 'shop-card--selected': selectedShopId === shop.shopId },
                    ]"
                    @click="selectShop(shop.shopId)"
                  >
                    <div class="shop-card__content">
                      <div
                        class="d-flex justify-content-between align-items-start mb-2"
                      >
                        <b-form-radio
                          v-model="selectedShopId"
                          :value="shop.shopId"
                          @click.native.stop
                        >
                        </b-form-radio>
                        <b-badge
                          v-if="shop.status"
                          :variant="
                            shop.status === 'active' ? 'success' : 'secondary'
                          "
                        >
                          {{
                            shop.status === 'active' ? 'Hoạt động' : shop.status
                          }}
                        </b-badge>
                      </div>

                      <h6 class="shop-card__name mb-2">{{ shop.shopName }}</h6>

                      <div class="shop-card__details">
                        <div class="mb-1">
                          <small class="text-muted">Mã gian hàng:</small>
                          <div class="font-weight-bold">{{ shop.shopId }}</div>
                        </div>

                        <div v-if="shop.sellerName" class="mb-1">
                          <small class="text-muted">Người bán:</small>
                          <div>{{ shop.sellerName }}</div>
                        </div>

                        <div v-if="shop.region" class="mb-1">
                          <small class="text-muted">Khu vực:</small>
                          <div>{{ shop.region }}</div>
                        </div>
                      </div>
                    </div>
                  </b-card>
                </b-col>
              </b-card-group>

              <div class="shop-selection-page__actions text-center">
                <b-button
                  variant="primary"
                  size="lg"
                  :disabled="!selectedShopId || isConfirming"
                  @click="confirmSelection"
                >
                  <b-spinner v-if="isConfirming" small class="mr-2"></b-spinner>
                  {{ isConfirming ? 'Đang xử lý...' : 'Tiếp tục' }}
                </b-button>
                <b-button
                  variant="light"
                  size="lg"
                  class="ml-2"
                  @click="cancelSelection"
                  :disabled="isConfirming"
                >
                  Hủy
                </b-button>
              </div>
            </div>

            <div v-else-if="!loading" class="text-center py-5">
              <i class="flaticon2-information icon-3x text-muted mb-3"></i>
              <h5 class="text-muted mb-3">Không tìm thấy gian hàng nào</h5>
              <p class="text-muted mb-4">
                Không có gian hàng nào được liên kết với tài khoản này.
              </p>
              <b-button variant="primary" @click="backToConnect">
                Quay lại
              </b-button>
            </div>
          </template>
        </KTCodePreview>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import Loader from '@/view/content/Loader.vue';
import { getToastInstance } from '@/utils/toastHelper';
import { mapGetters } from 'vuex';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';

const platformRepository = RepositoryFactory.get('platformIntegration');

export default {
  name: 'ShopSelectionPage',
  components: {
    KTCodePreview,
    Loader,
  },
  data() {
    return {
      shops: [],
      selectedShopId: null,
      loading: false,
      error: null,
      isConfirming: false,
      platform: null,
      configId: null,
    };
  },
  computed: {
    ...mapGetters(['layoutConfig']),
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'));
    },
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo');
    },
    platformName() {
      const names = {
        shopee: 'Shopee',
        tiktok: 'TikTok Shop',
      };
      return names[this.platform] || this.platform || 'sàn thương mại điện tử';
    },
  },
  async created() {
    this.loadBreadcrumb();
    this.extractParams();
    await this.loadShops();
  },
  methods: {
    ...getToastInstance(),

    loadBreadcrumb() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Kênh bán' },
        { title: 'Chọn gian hàng' },
      ]);
    },

    extractParams() {
      const query = this.$route.query;
      this.platform = query.platform;
      this.configId = query.configId;

      if (!this.platform) {
        this.error = 'Thiếu thông tin platform. Vui lòng thử lại.';
      }
    },

    async loadShops() {
      if (this.error) {
        return;
      }

      try {
        this.loading = true;
        this.error = null;

        const response = await platformRepository.getAvailableShops(
          this.platform,
        );
        this.shops = response.shops || response || [];

        if (this.shops.length === 1) {
          this.selectedShopId = this.shops[0].shopId;
        }
      } catch (error) {
        console.error('Error loading shops:', error);
        this.error = 'Không thể tải danh sách gian hàng. Vui lòng thử lại.';
        this.makeToastFailure('Lỗi khi tải danh sách gian hàng');
      } finally {
        this.loading = false;
      }
    },

    selectShop(shopId) {
      this.selectedShopId = shopId;
    },

    async confirmSelection() {
      if (!this.selectedShopId) {
        this.makeToastWarning('Vui lòng chọn một gian hàng');
        return;
      }

      try {
        this.isConfirming = true;

        const selectedShop = this.shops.find(
          (shop) => shop.shopId === this.selectedShopId,
        );

        this.$router.push({
          name: 'platform-integration-success',
          query: {
            configId: this.configId,
            shopId: this.selectedShopId,
            shopName: selectedShop?.shopName || '',
            platform: this.platform,
          },
        });
      } catch (error) {
        console.error('Error confirming selection:', error);
        this.makeToastFailure('Có lỗi xảy ra. Vui lòng thử lại.');
        this.isConfirming = false;
      }
    },

    cancelSelection() {
      this.$router.push({ name: 'platform-integration-list' });
    },

    backToConnect() {
      this.$router.push({ name: 'platform-integration-connect' });
    },

    makeToastWarning(message) {
      this.$bvToast.toast(message, {
        title: 'Cảnh báo',
        variant: 'warning',
        solid: true,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.shop-selection-page {
  padding: 2rem 0;

  &__header {
    h5 {
      font-weight: 600;
      color: #3f4254;
    }
  }

  &__shops {
    margin-top: 2rem;
  }

  &__actions {
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid #e4e6ef;
  }

  .icon-3x {
    font-size: 3rem;
    display: block;
  }
}

.shop-card {
  cursor: pointer;
  border: 2px solid #e4e6ef;
  transition: all 0.3s ease;
  height: 100%;

  &:hover {
    border-color: #3699ff;
    box-shadow: 0 4px 12px rgba(54, 153, 255, 0.15);
    transform: translateY(-2px);
  }

  &--selected {
    border-color: #3699ff;
    background-color: #f1faff;
    box-shadow: 0 4px 12px rgba(54, 153, 255, 0.2);
  }

  &__content {
    padding: 0.5rem;
  }

  &__name {
    font-weight: 600;
    color: #3f4254;
    min-height: 2.5rem;
  }

  &__details {
    font-size: 0.875rem;

    small {
      display: block;
      font-size: 0.75rem;
    }

    .font-weight-bold {
      font-family: monospace;
      font-size: 0.85rem;
    }
  }
}

:deep(.card-deck) {
  display: flex;
  flex-wrap: wrap;
  margin-right: -15px;
  margin-left: -15px;
}
</style>
